import {
    IParams,
} from './types';

import React, {
    FC,
    useState,
} from 'react';
import {
    useParams,
} from 'react-router-dom';

import {
    useAccountStatistic,
} from 'entities/account-statistics/hooks/use-account-statistic';
import {
    PAYMENT_STATUS,
    RESPONSE_ERROR,
} from 'entities/payments/constants';
import {
    usePayment,
} from 'entities/payments/hooks/use-payment';

import {
    Container,
    Loading,
} from 'components/other';

import Footer from './Footer';
import Header from './Header';
import PaymentForm, {
    usePaymentFormRef,
} from './PaymentForm';
import PendingStatusCard from './PendingStatusCard';
import StatusCard from './StatusCard';

import {
    formatClientFieldsError,
} from './helpers/format-client-fields-error';
import {
    getRedirectUrl,
} from './helpers/get-redirect-url';
import {
    isPendingUpiIntentQr,
} from './helpers/is-pending-upi-intent-qr';
import {
    useCurrentSeconds,
} from './hooks/use-current-seconds';
import {
    useDelayedRedirect,
} from './hooks/use-delayed-redirect';
import usePaymentLocale from './hooks/use-payment-locale';
import {
    usePaymentStatusSync,
} from './hooks/use-payment-status-sync';

import styles from './Payment.module.scss';

const Payment: FC = () => {
    const [isPolling, setIsPolling] = useState(false);
    const [isPendingState, setIsPendingState] = useState(false);
    const [isFailedState, setIsFailedState] = useState(false);

    const {
        accountStatistic,
        getAccountStatistic,
        cancelAccountStatistic,
    } = useAccountStatistic();
    const {
        createPayment,
    } = usePayment();

    const locale = usePaymentLocale();

    const {
        encodedId,
    } = useParams<IParams>();

    const onSubmit = async () => {
        if (!encodedId) {
            return;
        }
        if (!accountStatistic) {
            return;
        }
        if (!paymentFormRef.current.validate()) {
            return;
        }

        const dto = paymentFormRef.current.getDto();

        if (!dto) {
            return;
        }

        const res = await createPayment(dto);

        if (!res.payment || res.error) {
            if (res.error === RESPONSE_ERROR.NOT_UNIQUE_CLIENT_FIELDS_VALUES) {
                const error = formatClientFieldsError(accountStatistic.paymentMethod.paymentPageType);

                paymentFormRef.current.setError(error);

                return;
            }

            setIsFailedState(true);

            return;
        }

        await getAccountStatistic(encodedId, {
            locale,
        });
        setIsPolling(true);
    };

    const onCancel = async () => {
        if (!encodedId) {
            return;
        }

        await cancelAccountStatistic(encodedId);
    };

    const {isPending} = usePaymentStatusSync({
        encodedId: encodedId ?? null,
        accountStatistic,
        locale,
        isPolling,
        getAccountStatistic,
        onSubmit,
    });

    const currentSeconds = useCurrentSeconds(isPending);

    useDelayedRedirect(getRedirectUrl({
        accountStatistic,
        isPending,
        isFailed: isFailedState,
        currentSeconds,
    }));

    const paymentFormRef = usePaymentFormRef();

    if (!accountStatistic) {
        if (isPending) {
            return (
                <Container className={styles.loading}>
                    <Loading/>
                </Container>
            );
        }

        return (
            <Container>
                <Header
                    merchantLogoUrl={null}
                    isMerchantLogoDisplayedOnPaymentPage={false}
                    cancelUrl={null}
                    badgeType={'FAILED'}
                    currentSeconds={currentSeconds}
                />
                <StatusCard
                    type={'NOT_FOUND'}
                    url={null}
                />
                <Footer isLogoDisplayed={false}/>
            </Container>
        );
    }
    if (!accountStatistic.payment) {
        if (isFailedState) {
            return (
                <Container>
                    <Header
                        merchantLogoUrl={accountStatistic.merchant.logoUrl}
                        isMerchantLogoDisplayedOnPaymentPage={accountStatistic.merchant.isMerchantLogoDisplayedOnPaymentPage}
                        cancelUrl={accountStatistic.paymentPageInfo.cancelUrl}
                        onCancel={onCancel}
                        badgeType={'FAILED'}
                        currentSeconds={currentSeconds}
                    />
                    <StatusCard
                        type={'FAILED'}
                        url={accountStatistic.paymentPageInfo.failUrl}
                    />
                    <Footer isLogoDisplayed={accountStatistic.merchant.isLogoDisplayedOnPaymentPage}/>
                </Container>
            );
        }

        return (
            <Container>
                <Header
                    merchantLogoUrl={accountStatistic.merchant.logoUrl}
                    isMerchantLogoDisplayedOnPaymentPage={accountStatistic.merchant.isMerchantLogoDisplayedOnPaymentPage}
                    cancelUrl={accountStatistic.paymentPageInfo.cancelUrl}
                    onCancel={onCancel}
                    badgeType={'TIMER'}
                    currentSeconds={currentSeconds}
                />
                <PaymentForm
                    ref={paymentFormRef}
                    accountStatistic={accountStatistic}
                    onPending={() => setIsPendingState(true)}
                    onSubmit={onSubmit}
                />
                <Footer isLogoDisplayed={accountStatistic.merchant.isLogoDisplayedOnPaymentPage}/>
            </Container>
        );
    }
    if (isPendingUpiIntentQr(accountStatistic, isPendingState)) {
        return (
            <Container>
                <Header
                    merchantLogoUrl={accountStatistic.merchant.logoUrl}
                    isMerchantLogoDisplayedOnPaymentPage={accountStatistic.merchant.isMerchantLogoDisplayedOnPaymentPage}
                    cancelUrl={accountStatistic.paymentPageInfo.cancelUrl}
                    onCancel={onCancel}
                    badgeType={'TIMER'}
                    currentSeconds={currentSeconds}
                />
                <PaymentForm
                    ref={paymentFormRef}
                    accountStatistic={accountStatistic}
                    onPending={() => setIsPendingState(true)}
                    onSubmit={onSubmit}
                />
                <Footer isLogoDisplayed={accountStatistic.merchant.isLogoDisplayedOnPaymentPage}/>
            </Container>
        );
    }

    switch (isPendingState ? PAYMENT_STATUS.PENDING : accountStatistic.payment.status) {
        case PAYMENT_STATUS.PENDING:
        case PAYMENT_STATUS.IN_PROGRESS:
        case PAYMENT_STATUS.NEED_CHECK:
        case PAYMENT_STATUS.EXPIRED: {
            return (
                <Container>
                    <Header
                        merchantLogoUrl={accountStatistic.merchant.logoUrl}
                        isMerchantLogoDisplayedOnPaymentPage={accountStatistic.merchant.isMerchantLogoDisplayedOnPaymentPage}
                        cancelUrl={accountStatistic.paymentPageInfo.cancelUrl}
                        onCancel={onCancel}
                        badgeType={'PENDING'}
                        currentSeconds={currentSeconds}
                    />
                    <PendingStatusCard
                        amountKopecks={accountStatistic.amountKopecks}
                        currencySymbol={accountStatistic.paymentMethod.currencySymbol}
                        url={accountStatistic.paymentPageInfo.pendingUrl}
                    />
                    <Footer isLogoDisplayed={accountStatistic.merchant.isLogoDisplayedOnPaymentPage}/>
                </Container>
            );
        }
        case PAYMENT_STATUS.SUCCESS: {
            return (
                <Container>
                    <Header
                        merchantLogoUrl={accountStatistic.merchant.logoUrl}
                        isMerchantLogoDisplayedOnPaymentPage={accountStatistic.merchant.isMerchantLogoDisplayedOnPaymentPage}
                        cancelUrl={accountStatistic.paymentPageInfo.cancelUrl}
                        onCancel={onCancel}
                        badgeType={'SUCCESS'}
                        currentSeconds={currentSeconds}
                    />
                    <StatusCard
                        type={'SUCCESS'}
                        url={accountStatistic.paymentPageInfo.successUrl}
                    />
                    <Footer isLogoDisplayed={accountStatistic.merchant.isLogoDisplayedOnPaymentPage}/>
                </Container>
            );
        }
        case PAYMENT_STATUS.FAILED: {
            return (
                <Container>
                    <Header
                        merchantLogoUrl={accountStatistic.merchant.logoUrl}
                        isMerchantLogoDisplayedOnPaymentPage={accountStatistic.merchant.isMerchantLogoDisplayedOnPaymentPage}
                        cancelUrl={accountStatistic.paymentPageInfo.cancelUrl}
                        onCancel={onCancel}
                        badgeType={'FAILED'}
                        currentSeconds={currentSeconds}
                    />
                    <StatusCard
                        type={'FAILED'}
                        url={accountStatistic.paymentPageInfo.failUrl}
                    />
                    <Footer isLogoDisplayed={accountStatistic.merchant.isLogoDisplayedOnPaymentPage}/>
                </Container>
            );
        }
    }
};

export default Payment;
